/**
 * Prep — screen 06. Task definitions per venue, completions per service date.
 *
 * The only thing here that is not a plain shape is the *service date*, and it
 * is the reason this module exists rather than the screen doing `new Date()`.
 *
 * A bar's night does not end at midnight. The prototype's shift runs
 * 16:00–01:00 and the closing tasks are due at 01:30, so a tick at half past
 * one on Saturday morning belongs to Friday's service, not Saturday's. If the
 * API and the SPA disagree about which day that is, a closing task ticked at
 * the end of the night reappears unticked when the next shift opens — and the
 * PREPDONE# key it wrote is stranded on a date nobody looks at.
 *
 * So there is one definition, shared, and it is the venue's local day with a
 * rollover hour rather than UTC. Melbourne is UTC+10/+11 depending on the
 * date: at 01:30 on a summer Saturday it is still *Friday* in UTC, and in
 * winter at 09:00 Monday it is already Sunday there. Either way, taking
 * `toISOString().slice(0, 10)` gives the wrong answer for a Melbourne bar
 * roughly a third of the time, which is why nothing in this file does that.
 *
 * The zone arithmetic itself now lives in `time.ts`, because the week and the
 * month need exactly the same rule and were getting it wrong; what is left
 * here is prep's own vocabulary spelled in terms of it.
 */
import { addDays, civilDate, civilInstant, DEFAULT_TIMEZONE, isDateString, localDateIn, serviceDateIn, weekdayOf, } from "./time.js";
/** The zone a venue runs in unless its META names another. See `timezoneOf`. */
export const MELBOURNE = DEFAULT_TIMEZONE;
/** What the org's settings default to before anyone edits `prepTabs`. */
export const DEFAULT_PREP_TABS = [
    "Opening",
    "Batch prep",
    "Cleaning",
    "Closing",
];
/**
 * The wall-clock fields an instant shows in a timezone.
 *
 * `civilDate` spelled the way prep has always spelled it. Kept because the
 * seed and the tests read `year`/`month`/`day` off it.
 */
export function zonedParts(at, timeZone = MELBOURNE) {
    const civil = civilDate(at, timeZone);
    return {
        year: civil.y,
        month: civil.m,
        day: civil.d,
        hour: civil.hour,
        minute: civil.minute,
        second: civil.second,
    };
}
/** `YYYY-MM-DD` for an instant, as the calendar in `timeZone` reads it. */
export function localDate(at = new Date(), timeZone = MELBOURNE) {
    return localDateIn(at, timeZone);
}
/**
 * The service date an instant belongs to: the venue's local day, with
 * anything before `rolloverHour` counted back to the night before.
 *
 * `serviceDate(2026-09-19T15:30Z)` — 01:30 on Saturday in Melbourne — is
 * `2026-09-19`, Friday, because that is whose shift is still running.
 */
export function serviceDate(at = new Date(), options = {}) {
    // A venue's clock carries its own cut-off; a bare zone gets the default.
    return serviceDateIn(at, options.timeZone ?? MELBOURNE, options.rolloverHour);
}
/** The next `weekday` (0 = Sunday) strictly after `date`. */
export function nextWeekday(date, weekday) {
    const ahead = (weekday - weekdayOf(date) + 7) % 7;
    return addDays(date, ahead === 0 ? 7 : ahead);
}
/**
 * `YYYY-MM-DD` that is also a day the calendar has.
 *
 * The shape check is not enough on its own: `Date.parse` silently rolls
 * `2026-02-31` forward to 3 March rather than refusing it, so a service date
 * that does not exist would be accepted and then written to a key under a
 * *different* date. Round-tripping through the parse is what catches it.
 */
export function isRealDate(value) {
    if (!isDateString(value))
        return false;
    const at = new Date(`${value}T00:00:00Z`);
    return !Number.isNaN(at.getTime()) && at.toISOString().slice(0, 10) === value;
}
/**
 * The instant at which a wall-clock `HH:MM` falls in a timezone, as an ISO
 * string — the inverse of `zonedParts`. `civilInstant` does the iterating.
 */
export function zonedInstant(date, time, timeZone = MELBOURNE) {
    const match = /^(\d{2}):(\d{2})$/.exec(time);
    if (!isDateString(date) || !match) {
        throw new RangeError(`Not a local date and time: ${date} ${time}`);
    }
    return civilInstant(date, Number(match[1]), Number(match[2]), timeZone).toISOString();
}
/** Is the task's start-by time behind us? Amber in the UI when it is. */
export function isOverdue(dueAt, now = new Date()) {
    const due = Date.parse(dueAt);
    return Number.isFinite(due) && due < now.getTime();
}
/**
 * Order within a tab: the manager's `sortOrder`, then title, so two tasks
 * added at the same order do not swap places between reads.
 */
export function comparePrepTasks(a, b) {
    return a.sortOrder !== b.sortOrder
        ? a.sortOrder - b.sortOrder
        : a.title.localeCompare(b.title);
}
//# sourceMappingURL=prep.js.map